/**
 * Whether a failed token renewal means the Firebase account NO LONGER EXISTS,
 * as opposed to any of the ordinary reasons a renewal fails.
 *
 * WHY THIS IS A SEPARATE, NARROW QUESTION. The answer decides whether somebody
 * is signed out. Getting it wrong in one direction leaves three members in a
 * loop they cannot leave; getting it wrong in the other signs people out during
 * a Firebase hiccup, on a plane, or in a lift. So this recognises ONE
 * situation, the account being gone, and says no to everything else, including
 * every failure it has not seen before.
 *
 * WHAT WAS OBSERVED. CAPACITOR-R, in production: five occurrences, three
 * members, between 21 and 25 September 2026, all on /home/profile/edit, all
 * carrying the tag token_renewal=missing that the renewal diagnostic attaches.
 * CAPACITOR-D, the iOS plugin's opaque « RuntimeError erreur 1 », fires at the
 * same second on the same route, roughly every five minutes: a renewal loop
 * that can never succeed, because what it is renewing was deleted.
 *
 * THIS IS NOT « NO USER IS SIGNED IN ». That one is a race, Firebase restoring
 * its user from the keychain, and the renewal path already waits for it; see
 * refreshToken in auth.state.ts. A race must never sign anybody out. The two
 * messages are different and are kept different here on purpose.
 */

/** What Firebase calls it. The only code this accepts. */
const THE_ACCOUNT_IS_GONE = 'auth/user-not-found';

/**
 * The sentence the SDK sends when there is no code to read.
 *
 * MATCHED ON TEXT, WHICH IS NORMALLY A BAD IDEA, and is done here because the
 * Capacitor plugin unwraps the error on iOS and the code does not always
 * survive: CAPACITOR-R arrives as an Error carrying this message and nothing
 * else. The sentence is Firebase's own and is not translated by the SDK.
 *
 * It is deliberately the whole distinctive phrase and not a loose keyword. A
 * match on « deleted » alone would catch a deleted GROUP, a deleted activity,
 * and sign somebody out for it.
 */
const THE_SENTENCE = 'no user record corresponding to this identifier';

export const theFirebaseAccountIsGone = (error: unknown): boolean => {
  if (error === null || error === undefined) {
    return false;
  }

  const code = (error as { code?: unknown })?.code;
  if (typeof code === 'string' && code === THE_ACCOUNT_IS_GONE) {
    return true;
  }

  const message = (error as { message?: unknown })?.message;
  if (typeof message === 'string' && message.toLowerCase().includes(THE_SENTENCE)) {
    return true;
  }

  return typeof error === 'string' && error.toLowerCase().includes(THE_SENTENCE);
};
